/**
 * 플랫폼 API — `docs/api/studyResultOld.json` 실습기록·랩 등
 *
 * 베이스: `getStudyResultPlatformBaseUrl()` — env 미설정 시 기본 `https://api2_test.medicrew.me` 로 실제 `fetch` (목업 없음).
 */

import { debugApiLog } from '@/config/debugApiLog';
import { isStudyResultDevLogEnabled, logStudyResultApiTurn } from '@/config/studyResultDevConsoleLog';
import { getStudyResultPlatformBaseUrl } from '@/config/studyResultPlatformBaseUrl';
import apiClient from './apiClient';

/** [API_연동_가이드] 실습기록·시나리오 플랫폼(api2) 공통. `REACT_APP_MEDICREW_CLIENT_VERSION`으로 덮기 가능 */
const MEDICREW_PLATFORM_CLIENT_VERSION = process.env.REACT_APP_MEDICREW_CLIENT_VERSION?.trim() || '2.4.0';

export class PlatformApiError extends Error {
	constructor(
		public status: number,
		public statusText: string,
		public data?: unknown,
		message?: string
	) {
		super(message || `Platform API Error: ${status} ${statusText}`);
		this.name = 'PlatformApiError';
	}
}

export interface PlatformRequestOptions extends RequestInit {
	classId?: string | number;
	params?: Record<string, string | number | boolean | undefined>;
	timeout?: number;
	/**
	 * 지정되면 `fetch` Authorization에만 사용 (Bearer 접두사 없이, 필요시 URL 인코딩).
	 * `''`이면 Authorization 헤더 없음 — 이 경우에도 저장된 액세스 토큰으로 폴백하지 않음.
	 * 생략(`undefined`)이면 기존처럼 `apiClient.getAccessToken()` 사용.
	 */
	accessToken?: string;
}

function unwrapPlatformJson<T>(raw: unknown): T {
	if (raw == null) return raw as T;
	if (typeof raw !== 'object') return raw as T;
	const o = raw as Record<string, unknown>;
	if ('data' in o && o.data !== undefined) {
		return o.data as T;
	}
	return raw as T;
}

/**
 * `platformGet`과 동일 규칙으로 최종 GET URL 문자열 생성 (데모·로그용)
 */
export function buildPlatformGetUrl(
	path: string,
	options: Pick<PlatformRequestOptions, 'params'> = {}
): string {
	const base = getStudyResultPlatformBaseUrl();
	const pathClean = path.replace(/^\/+/, '');
	let url = `${base.replace(/\/+$/, '')}/${pathClean}`;

	if (options.params && Object.keys(options.params).length > 0) {
		const sp = new URLSearchParams();
		for (const [key, value] of Object.entries(options.params)) {
			if (value !== undefined && value !== null && value !== '') {
				sp.append(key, typeof value === 'boolean' ? String(value) : String(value));
			}
		}
		const q = sp.toString();
		if (q) url += `?${q}`;
	}
	return url;
}

/** `platformGet`이 붙이는 medicrew web 공통 헤더(데모·로그·문서와 동기화) */
function platformMedicrewWebHeaderLines(): string[] {
	return [
		'x-access-from-medicrew-web: true',
		'x-medicrew-type: MEDICREW',
		`x-client-version: ${MEDICREW_PLATFORM_CLIENT_VERSION}`,
	];
}

/**
 * `platformGet`이 실제로 병합하는 medicrew web 공통 헤더(학습·실습기록 플랫폼 전용)
 */
export function applyPlatformMedicrewWebHeaders(headers: Record<string, string>): void {
	headers['x-access-from-medicrew-web'] = 'true';
	headers['x-medicrew-type'] = 'MEDICREW';
	headers['x-client-version'] = MEDICREW_PLATFORM_CLIENT_VERSION;
}

/**
 * 학습 결과(`SimulationResults` 등)에서 웹 BFF(`apiClient` → `/scenario/...`, `/scenario-play-record`)에도
 * [API_연동_가이드] medicrew web + (클래스 맥락이 있으면) `x-class-id` 를 붙인다. `classId`가 비어 있으면 빈 객체.
 */
export function buildStudyResultWebApiContextHeaders(
	classId: string | number | null | undefined
): Record<string, string> {
	if (classId == null || String(classId).trim() === '') {
		return {};
	}
	const headers: Record<string, string> = {};
	applyPlatformMedicrewWebHeaders(headers);
	headers['x-class-id'] = String(classId).trim();
	return headers;
}

/**
 * 호출 요약: GET URL + [API_연동_가이드] 공통 헤더 + (선택) `x-class-id` (URL에는 안 붙음)
 */
export function describePlatformGetRequest(path: string, options: PlatformRequestOptions = {}): string[] {
	const lines: string[] = [`GET ${buildPlatformGetUrl(path, options)}`, ...platformMedicrewWebHeaderLines()];
	if (options.classId != null && String(options.classId).trim() !== '') {
		lines.push(`x-class-id: ${String(options.classId)}`);
	}
	return lines;
}

/**
 * GET — 항상 실제 네트워크 호출 (베이스는 env 또는 api2 테스트 기본값)
 */
export async function platformGet<T = unknown>(
	path: string,
	options: PlatformRequestOptions = {}
): Promise<T> {
	const url = buildPlatformGetUrl(path, options);

	const headers: Record<string, string> = {
		Accept: 'application/json',
	};
	applyPlatformMedicrewWebHeaders(headers);
	
	// Authorization: 플랫폼 API는 Bearer 접두사 없이 암호화된 토큰을 직접 전송
	// 웹 로그인 JWT는 URL 인코딩이 필요할 수 있음
	if (options.accessToken !== undefined) {
		const token = String(options.accessToken).trim();
		if (token) {
			// 이미 암호화된 토큰이면 그대로, JWT면 URL 인코딩
			const needsEncoding = !token.includes('%');
			headers.Authorization = needsEncoding ? encodeURIComponent(token) : token;
		}
	} else {
		const token = apiClient.getAccessToken();
		if (token) {
			// 웹 로그인 JWT는 URL 인코딩 필요
			const needsEncoding = !token.includes('%');
			headers.Authorization = needsEncoding ? encodeURIComponent(token) : token;
		}
	}
	
	if (options.classId != null && String(options.classId).trim() !== '') {
		headers['x-class-id'] = String(options.classId);
	}

	const timeoutMs = options.timeout ?? 45_000;
	const controller = new AbortController();
	const tid = setTimeout(() => controller.abort(), timeoutMs);

	if (isStudyResultDevLogEnabled()) {
		logStudyResultApiTurn('req', 'platform GET', {
			method: 'GET',
			url,
			params: options.params,
			headers,
		});
	}

	try {
		const res = await fetch(url, {
			method: 'GET',
			headers,
			signal: controller.signal,
		});

		const text = await res.text();
		let parsed: unknown;
		try {
			parsed = text ? JSON.parse(text) : null;
		} catch {
			if (isStudyResultDevLogEnabled()) {
				logStudyResultApiTurn('err', 'platform GET', {
					url,
					status: res.status,
					body: text,
					err: 'Platform API 응답이 JSON이 아닙니다.',
				});
			}
			throw new PlatformApiError(res.status, res.statusText, text, 'Platform API 응답이 JSON이 아닙니다.');
		}

		if (!res.ok) {
			if (isStudyResultDevLogEnabled()) {
				logStudyResultApiTurn('err', 'platform GET', {
					url,
					status: res.status,
					body: parsed,
					err: res.statusText,
				});
			}
			throw new PlatformApiError(
				res.status,
				res.statusText,
				parsed,
				typeof parsed === 'object' && parsed != null && 'message' in (parsed as object)
					? String((parsed as { message?: string }).message)
					: `Platform API ${res.status}`
			);
		}

		const out = unwrapPlatformJson<T>(parsed);
		if (isStudyResultDevLogEnabled()) {
			logStudyResultApiTurn('res', 'platform GET', { method: 'GET', url, status: res.status, body: out });
		}
		debugApiLog('[platform] GET', url, '←', out);
		return out;
	} catch (e) {
		if (e instanceof PlatformApiError) throw e;
		if (e instanceof Error && e.name === 'AbortError') {
			if (isStudyResultDevLogEnabled()) {
				logStudyResultApiTurn('err', 'platform GET', { url, err: '요청 시간이 초과(AbortError)' });
			}
			throw new PlatformApiError(0, 'Aborted', undefined, '요청 시간이 초과되었습니다.');
		}
		if (isStudyResultDevLogEnabled()) {
			logStudyResultApiTurn('err', 'platform GET', { url, err: e });
		}
		throw e;
	} finally {
		clearTimeout(tid);
	}
}
